import { Box } from "@aksara-ui/react";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";

import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const REPORT_ORIGIN = "https://ancol-analytics.kata.ai";

const UltraRewardContent: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 576 });

  const frame = useRef<HTMLIFrameElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // axios.defaults.withCredentials is already true (src/service/Api/index.ts),
  // so the access_token cookie the jwt middleware reads is sent automatically.
  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await axios.get("/admin/analytics/ancol-embed-url");
      setSrc(res.data.data.url);
    } catch (e) {
      setError("The Ancol report could not be loaded.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Both checks matter. Origin rejects other sites; source rejects other
      // frames and popups on this origin. Without them any page could tell the
      // CMS that the report's session had expired.
      if (event.origin !== REPORT_ORIGIN) return;
      if (event.source !== frame.current?.contentWindow) return;

      const msg = event.data as { type?: string; detail?: string };
      if (msg?.type === "ancol:session-expired") void load(); // mint a fresh one
      if (msg?.type === "ancol:embed-refused") setError(msg.detail ?? "Refused.");
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [load]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      {error && <p role="alert">{error}</p>}
      {src && (
        <iframe
          ref={frame}
          title="Ancol loyalty report"
          src={src}
          allow="fullscreen"
          referrerPolicy="no-referrer"
          style={{
            width: "100%",
            height: "calc(100vh - 160px)",
            border: "none",
            display: "block",
          }}
        />
      )}
    </Box>
  );
};

export default UltraRewardContent;